import React, { useState, useEffect } from "react";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import MessagesReceived from "./MessagesReceived";
import MessagesSent from "./MessagesSent";
import GroupMessages from "./GroupMessages";
import CreateMessageForm from "./CreateMessageForm";
import ContactFormDisplay from "./ContactFormDisplay";
import Button from "./../../components/Button/Button";
import { LuMessageSquarePlus } from "react-icons/lu";
import { FaInbox, FaLayerGroup, FaPaperPlane } from "react-icons/fa";
import { MdMessage } from "react-icons/md";
import useScrollToTop from "./../../hooks/useScrollToTop";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import { ToastContainer } from "react-toastify";
import { useMessage } from "./../../contexts/MessageContext";
import { useContactForm } from "./../../contexts/ContactFormContext";
import PageHeader from "../../components/Layout/PageHeader";

const MessagesPage = () => {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId;

  const isAdmin = userRole === "Administrateur" || userRole === "Bureau";
  const isAdminOrTeacher = ["Administrateur", "Bureau", "Membres"].includes(
    userRole
  );

  const { unreadMessageCount } = useMessage();
  const { unreadCountContactForm } = useContactForm();

  const [showForm, setShowForm] = useState(false);
  const [refreshMessages, setRefreshMessages] = useState(false);
  const [activeTab, setActiveTab] = useState("received");

  useEffect(() => {
    // debug éventuel
    console.log("userRole", userRole);
    console.log("userId", userId);
  }, [userRole, userId]);

  const handleNewMessage = () => {
    setShowForm(true);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setRefreshMessages(true);
  };

  const hideForm = () => {
    setShowForm(false);
  };

  const tabs = [
    {
      id: "received",
      label: "Reçus",
      icon: FaInbox,
      badge: unreadMessageCount,
    },
    {
      id: "sent",
      label: "Envoyés",
      icon: FaPaperPlane,
    },
    ...(isAdmin
      ? [
          {
            id: "contact",
            label: "Formulaire",
            icon: MdMessage,
            badge: unreadCountContactForm,
          },
        ]
      : []),
    ...(isAdminOrTeacher
      ? [
          {
            id: "group",
            label: "Groupes",
            icon: FaLayerGroup,
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      {/* Titre page */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4">
        <PageHeader
          eyebrow="Utilisateurs connectés"
          title="Messagerie"
          subtitle="Sur cette page, vous pouvez envoyer des messages aux responsables (CTF, Secrétaire, Président(e), membres de commissions…) et consulter vos messages reçus, vos envois, les messages groupés et ceux issus du formulaire de contact."
          align="center"
          size="lg"
        />
      </div>

      {/* Zone principale */}
      <div className="max-w-6xl mx-auto w-full mt-4 px-3 sm:px-4 space-y-4">
        {/* Bouton + formulaire nouveau message */}
        <div className="bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 rounded-2xl shadow-sm p-4 sm:p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 mb-1">
                Nouveau message
              </p>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                Rédigez un message à un responsable ou à un membre de la ligue.
              </p>
            </div>
            <Button
              text="Nouveau message"
              onClick={handleNewMessage}
              icon={LuMessageSquarePlus}
              className="gap-2 text-xs sm:text-sm"
              hoverLabel="Créer un nouveau message"
            />
          </div>

          {showForm && (
            <div className="mt-4">
              <CreateMessageForm
                userId={userId}
                userRole={userRole}
                onSuccess={handleSuccess}
                show={showForm}
                onHide={hideForm}
              />
            </div>
          )}
        </div>

        {/* Onglets + panneaux */}
        <div className="bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 rounded-2xl shadow-sm">
          {/* Onglets */}
          <div className="border-b border-slate-200 dark:border-slate-700 px-3 sm:px-4 pt-3">
            <div className="inline-flex flex-wrap gap-2 sm:gap-3">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold transition-colors ${
                      isActive
                        ? "bg-primary text-white shadow-sm"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                    }`}
                  >
                    <Icon className="text-sm" />
                    <span>{tab.label}</span>
                    {tab.badge > 0 && (
                      <span
                        className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] rounded-full ${
                          isActive
                            ? "bg-white/90 text-primary"
                            : "bg-primary text-white"
                        }`}
                      >
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contenu onglet */}
          <div className="p-3 sm:p-5">
            {activeTab === "received" && (
              <MessagesReceived
                userId={userId}
                refresh={refreshMessages}
                setRefresh={setRefreshMessages}
              />
            )}

            {activeTab === "sent" && (
              <MessagesSent
                userId={userId}
                refresh={refreshMessages}
                setRefresh={setRefreshMessages}
              />
            )}

            {activeTab === "contact" && isAdmin && (
              <ContactFormDisplay userId={userId} />
            )}

            {activeTab === "group" && isAdminOrTeacher && (
              <GroupMessages userId={userId} />
            )}
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default MessagesPage;
